import { useEffect, useState } from "react";
import { MapContainer, TileLayer, GeoJSON, useMap, LayersControl, CircleMarker, Popup } from "react-leaflet";
import { Spin } from "antd";
import type { LatLngBoundsExpression } from "leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { apiClient } from "../../../services/api/apiClient";

// Fix for default marker icons in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

interface ControlPointLocationMapProps {
  farmId: number;
  controlPointId?: string;
  geometry?: GeoJSON.Geometry;
}

interface MapFeature {
  type: "Feature";
  geometry: any;
  properties: {
    layer_type: "control_point" | "farm_plot" | "audited_plot";
    id: string | number;
    name: string;
    [key: string]: any;
  };
}

// Component to handle map bounds adjustment
const MapBoundsHandler = ({ bounds }: { bounds: LatLngBoundsExpression | undefined }) => {
  const map = useMap();
  
  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [bounds, map]);
  
  return null;
};

export const ControlPointLocationMap = ({ controlPointId, farmId, geometry }: ControlPointLocationMapProps) => {
  const [bounds, setBounds] = useState<LatLngBoundsExpression | undefined>(undefined);
  const [mapData, setMapData] = useState<GeoJSON.FeatureCollection | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch all map data from single endpoint
  useEffect(() => {
    const fetchMapData = async () => {
      if (!farmId) return;
      
      try {
        setIsLoading(true);
        const response = await apiClient.get(`/farms/${farmId}/map-data/`);
        
        if (response.data) {
          setMapData(response.data);
          
          // Calculate bounds from all features
          const allBounds = L.latLngBounds([]);
          
          if (response.data.features) {
            response.data.features.forEach((feature: MapFeature) => {
              if (feature.geometry) {
                const layer = L.geoJSON(feature.geometry);
                allBounds.extend(layer.getBounds());
              }
            });
          }
          
          if (allBounds.isValid()) {
            setBounds(allBounds);
          }
        }
      } catch (error) {
        console.error("Error fetching farm map data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMapData();
  }, [farmId]);

  // Style based on layer type
  const getFeatureStyle = (feature: any) => {
    const layerType = feature.properties?.layer_type;
    
    switch (layerType) {
      case "control_point":
        return {
          fillColor: "#ff4d4f",
          weight: 3,
          opacity: 1,
          color: "#cf1322",
          fillOpacity: 0.6,
          radius: 8,
        };
      case "farm_plot":
        return {
          fillColor: "#1890ff",
          weight: 2,
          opacity: 1,
          color: "#096dd9",
          fillOpacity: 0.2,
        };
      case "audited_plot":
        return {
          fillColor: "#52c41a",
          weight: 2,
          opacity: 1,
          color: "#389e0d",
          fillOpacity: 0.3,
        };
      default:
        return {
          fillColor: "#d9d9d9",
          weight: 2,
          opacity: 1,
          color: "#8c8c8c",
          fillOpacity: 0.2,
        };
    }
  };

  // Popup content based on layer type
  const onEachFeature = (feature: any, layer: any) => {
    if (!feature.properties) return;
    
    const props = feature.properties;
    let popupContent = `<div><strong>${props.name || "Sin nombre"}</strong><br/>`;
    
    switch (props.layer_type) {
      case "control_point":
        popupContent += `Tipo: Punto de Control<br/>`;
        if (props.reason) popupContent += `Motivo: ${props.reason}<br/>`;
        if (props.status) popupContent += `Estado: ${props.status === "open" ? "Abierto" : "Cerrado"}`;
        break;
      case "farm_plot":
        popupContent += `Tipo: Parcela de Finca<br/>`;
        if (props.surface) popupContent += `Superficie: ${props.surface.toFixed(2)} ha`;
        break;
      case "audited_plot":
        popupContent += `Tipo: Parcela Auditada<br/>`;
        if (props.surface) popupContent += `Superficie: ${props.surface.toFixed(2)} ha<br/>`;
        if (props.audit_reference) popupContent += `Auditoría: ${props.audit_reference}`;
        break;
    }
    
    popupContent += `</div>`;
    layer.bindPopup(popupContent);
  };

  // Filter features by layer type
  const getFeaturesByType = (type: string): GeoJSON.FeatureCollection | null => {
    if (!mapData) return null;
    return {
      type: "FeatureCollection" as const,
      features: (mapData.features as any[]).filter((f: any) => f.properties?.layer_type === type),
    } as GeoJSON.FeatureCollection;
  };

  const defaultCenter: [number, number] = [39.4699, -0.3763]; // Valencia, Spain

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "500px" }}>
        <Spin size="large" />
      </div>
    );
  }

  const controlPoints = getFeaturesByType("control_point");
  const farmPlots = getFeaturesByType("farm_plot");
  const auditedPlots = getFeaturesByType("audited_plot");

  return (
    <MapContainer
      center={defaultCenter}
      zoom={15}
      style={{ height: "500px", width: "100%" }}
      scrollWheelZoom={true}
    >
      <LayersControl position="topright">
        {/* Satellite Layer */}
        <LayersControl.BaseLayer checked name="Satélite">
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            attribution='&copy; <a href="https://www.esri.com/">Esri</a>'
          />
        </LayersControl.BaseLayer>

        {/* Street Map Layer */}
        <LayersControl.BaseLayer name="Mapa">
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          />
        </LayersControl.BaseLayer>

        {/* Farm Plots Layer */}
        {farmPlots && farmPlots.features.length > 0 && (
          <LayersControl.Overlay checked name="Parcelas de la Finca">
            <GeoJSON
              data={farmPlots}
              style={getFeatureStyle}
              onEachFeature={onEachFeature}
            />
          </LayersControl.Overlay>
        )}

        {/* Audited Plots Layer */}
        {auditedPlots && auditedPlots.features.length > 0 && (
          <LayersControl.Overlay checked name="Parcelas Auditadas">
            <GeoJSON
              data={auditedPlots}
              style={getFeatureStyle}
              onEachFeature={onEachFeature}
            />
          </LayersControl.Overlay>
        )}

        {/* Control Points Layer */}
        {controlPoints && controlPoints.features.length > 0 && (
          <LayersControl.Overlay checked name="Puntos de Control">
            <GeoJSON
              data={controlPoints}
              style={getFeatureStyle}
              onEachFeature={onEachFeature}
              pointToLayer={(feature, latlng) => {
                return L.circleMarker(latlng, getFeatureStyle(feature) as any);
              }}
            />
          </LayersControl.Overlay>
        )}
      </LayersControl>

      {/* Adjust bounds */}
      <MapBoundsHandler bounds={bounds} />
    </MapContainer>
  );
};
